/**
 * Templates de véhicules basés sur les pictogrammes
 * Données issues de la maquette Excel "parc CAMION"
 */

export interface VehicleTemplate {
  id: string;
  name: string;
  category: string;
  image: string;
  vehicleType: string;
  fuelType: string | null;
  
  // === DIMENSIONS & CAPACITÉS UTILES ===
  payloadCapacity?: number; // Charge utile (kg)
  volumeCapacity?: number; // Capacité volume (m³)
  innerLength?: number; // Longueur utile intérieure (m)
  innerWidth?: number; // Largeur utile intérieure (m)
  innerHeight?: number; // Hauteur utile intérieure (m)
  compartmentsCount?: number; // Nombre de compartiments
  palletsEurope?: number; // Capacité palettes Europe
  palletsISO?: number; // Capacité palettes ISO
  
  // Groupe frigorifique
  hasFrigo?: boolean;
  frigoType?: "gasoil" | "electrique";
  frigoTankCapacity?: number;
  frigoBatteryAutonomy?: number;
  
  // Équipements
  hasHayon?: boolean;
  tailgateCapacity?: number; // Capacité hayon (kg)
  hasGrue?: boolean;
  craneCapacity?: number; // Capacité grue (kg)
  hasCiterne?: boolean;
  hasADR?: boolean;
  hasTachygraphe?: boolean;
  hasATP?: boolean;
  hasTemperatureRecorder?: boolean;
  hasWeighingInstruments?: boolean;
  
  // Valeurs par défaut réservoirs
  defaultFuelTankCapacity?: number;
  defaultAdBlueTankCapacity?: number;
  defaultAdBlueRatio?: number;
  
  // Contrôles à activer
  controls: {
    technicalInspection?: boolean;
    pollutionControl?: boolean;
    technicalInspectionPL?: boolean;
    tachograph?: boolean;
    tailgate?: boolean;
    crane?: boolean;
    extinguisher?: boolean;
    atp?: boolean;
    temperatureRecorder?: boolean;
    adr?: boolean;
    adrIntermediate?: boolean;
    adrPeriodic?: boolean;
    adrHoses?: boolean;
    weighingInstruments?: boolean;
    otherEquipment?: boolean;
  };
  
  description?: string;
  equipments?: string[];
}

export const vehicleTemplates: VehicleTemplate[] = [
  // ==========================================
  // VÉHICULES DE MOINS DE 3T5 (Données Excel)
  // ==========================================
  {
    id: "vul_caisse_18m3",
    name: "Fourgon Caisse 18m³ (25 véh.)",
    category: "VUL - Utilitaires < 3T5",
    image: "/bibliotheque_vehicules/02_VUL_Fourgons/image21.png",
    vehicleType: "vul",
    fuelType: "diesel",
    description: "25 véhicules dans le parc",
    // DONNÉES EXCEL LIGNE 1
    payloadCapacity: 848,
    volumeCapacity: 18,
    innerLength: 4.30,
    innerWidth: 2.08,
    innerHeight: 2.01,
    compartmentsCount: 2,
    palletsEurope: 8,
    palletsISO: 7,
    hasHayon: false,
    hasGrue: false,
    defaultFuelTankCapacity: 80,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      extinguisher: true,
    },
    equipments: ["Transpalette", "Sangles"],
  },
  {
    id: "vul_fourgon_6m3",
    name: "Fourgon 6m³ (12 véh.)",
    category: "VUL - Utilitaires < 3T5",
    image: "/bibliotheque_vehicules/02_VUL_Fourgons/image45.png",
    vehicleType: "vul",
    fuelType: "diesel",
    description: "12 véhicules dans le parc",
    // DONNÉES EXCEL LIGNE 2
    payloadCapacity: 900,
    volumeCapacity: 6,
    innerLength: 2.70,
    innerWidth: 1.65,
    innerHeight: 1.38,
    compartmentsCount: 1,
    palletsEurope: 2,
    palletsISO: 1,
    hasHayon: false,
    hasGrue: false,
    defaultFuelTankCapacity: 60,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      extinguisher: true,
    },
    equipments: ["Cloison de séparation"],
  },
  {
    id: "vul_fourgon_10m3_hayon",
    name: "Fourgon 10m³ + Hayon 500kg (7 véh.)",
    category: "VUL - Utilitaires < 3T5",
    image: "/bibliotheque_vehicules/02_VUL_Fourgons/image51.png",
    vehicleType: "vul",
    fuelType: "diesel",
    description: "7 véhicules dans le parc - Avec hayon",
    // DONNÉES EXCEL LIGNE 3
    payloadCapacity: 1000,
    volumeCapacity: 10,
    innerLength: 2.80,
    innerWidth: 1.75,
    innerHeight: 1.80,
    compartmentsCount: 1,
    palletsEurope: 3,
    palletsISO: 2,
    hasHayon: true,
    tailgateCapacity: 500,
    hasGrue: false,
    defaultFuelTankCapacity: 70,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      tailgate: true,
      extinguisher: true,
    },
    equipments: ["Hayon 500kg", "Points d'arrimage"],
  },
  {
    id: "vul_fourgon_10m3_1500kg",
    name: "Fourgon 10m³ - 1500kg (2 véh.)",
    category: "VUL - Utilitaires < 3T5",
    image: "/bibliotheque_vehicules/02_VUL_Fourgons/image11.png",
    vehicleType: "vul",
    fuelType: "diesel",
    description: "2 véhicules dans le parc - Charge lourde",
    // DONNÉES EXCEL LIGNE 4
    payloadCapacity: 1500,
    volumeCapacity: 10,
    innerLength: 2.80,
    innerWidth: 1.75,
    innerHeight: 1.80,
    compartmentsCount: 1,
    palletsEurope: 3,
    palletsISO: 2,
    hasHayon: false,
    hasGrue: false,
    defaultFuelTankCapacity: 70,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      extinguisher: true,
    },
    equipments: ["Charge lourde", "Sangles renforcées"],
  },

  // ==========================================
  // VÉHICULES POIDS LOURDS (Données Excel)
  // ==========================================
  {
    id: "pl_porteur_15m3",
    name: "Porteur 15m³ - 2487kg (2 véh.)",
    category: "PL Porteur Fourgon",
    image: "/bibliotheque_vehicules/04_Porteurs_Fourgon/image17.png",
    vehicleType: "pl_fourgon",
    fuelType: "diesel",
    description: "2 véhicules dans le parc",
    // DONNÉES EXCEL PL LIGNE 1
    payloadCapacity: 2487,
    volumeCapacity: 15,
    compartmentsCount: 2,
    hasHayon: false,
    hasGrue: false,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 200,
    defaultAdBlueTankCapacity: 40,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      extinguisher: true,
    },
    equipments: ["Tachygraphe"],
  },
  {
    id: "pl_porteur_34m3",
    name: "Porteur 34m³ - 5000kg (9 véh.)",
    category: "PL Porteur Fourgon",
    image: "/bibliotheque_vehicules/04_Porteurs_Fourgon/image25.png",
    vehicleType: "pl_fourgon",
    fuelType: "diesel",
    description: "9 véhicules dans le parc",
    // DONNÉES EXCEL PL LIGNE 2
    payloadCapacity: 5000,
    volumeCapacity: 34,
    innerLength: 6.20,
    innerWidth: 2.50,
    innerHeight: 2.20,
    compartmentsCount: 1,
    palletsEurope: 15,
    hasHayon: false,
    hasGrue: false,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 300,
    defaultAdBlueTankCapacity: 60,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      extinguisher: true,
    },
    equipments: ["Tachygraphe", "Rails aluminium"],
  },
  {
    id: "pl_porteur_hayon_500kg",
    name: "Porteur + Hayon 500kg (1 véh.)",
    category: "PL Porteur Fourgon",
    image: "/bibliotheque_vehicules/04_Porteurs_Fourgon/image16.png",
    vehicleType: "pl_fourgon",
    fuelType: "diesel",
    description: "1 véhicule dans le parc - Avec hayon",
    // DONNÉES EXCEL PL LIGNE 3
    payloadCapacity: 9560,
    innerLength: 7.60,
    compartmentsCount: 4,
    hasHayon: true,
    tailgateCapacity: 500,
    hasGrue: false,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 300,
    defaultAdBlueTankCapacity: 60,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      tailgate: true,
      extinguisher: true,
    },
    equipments: ["Hayon 500kg", "Tachygraphe"],
  },
  {
    id: "pl_porteur_grue_1500kg",
    name: "Porteur + Grue 1500kg (4 véh.)",
    category: "PL Porteur Fourgon",
    image: "/bibliotheque_vehicules/08_Porteurs_Grue/image8.png",
    vehicleType: "pl_fourgon",
    fuelType: "diesel",
    description: "4 véhicules dans le parc - Avec grue",
    // DONNÉES EXCEL PL LIGNE 4
    payloadCapacity: 5600,
    innerLength: 7.60,
    compartmentsCount: 6,
    hasHayon: false,
    hasGrue: true,
    craneCapacity: 1500,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 300,
    defaultAdBlueTankCapacity: 60,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      crane: true,
      extinguisher: true,
    },
    equipments: ["Grue 1500kg", "Stabilisateurs", "Télécommande"],
  },

  // ==========================================
  // VL - VÉHICULES LÉGERS
  // ==========================================
  {
    id: "vl_berline",
    name: "Voiture de service",
    category: "VL / Autocar",
    image: "/bibliotheque_vehicules/01_VL_Voitures/image10.png",
    vehicleType: "vl_autocar",
    fuelType: "diesel",
    defaultFuelTankCapacity: 50,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      extinguisher: true,
    },
    description: "Véhicule léger de service",
    equipments: ["Gilet jaune", "Triangle"],
  },
  {
    id: "vl_pickup",
    name: "Pick-up",
    category: "VL / Autocar",
    image: "/bibliotheque_vehicules/01_VL_Voitures/image50.png",
    vehicleType: "vl_autocar",
    fuelType: "diesel",
    payloadCapacity: 500,
    defaultFuelTankCapacity: 80,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      extinguisher: true,
    },
    description: "Pick-up utilitaire",
    equipments: ["Benne", "Ridelles"],
  },

  // ==========================================
  // VUL FRIGORIFIQUES
  // ==========================================
  {
    id: "vul_frigo_electrique",
    name: "VUL Frigo Électrique",
    category: "VUL - Utilitaires < 3T5",
    image: "/bibliotheque_vehicules/03_VUL_Frigorifiques/image20.png",
    vehicleType: "vul",
    fuelType: "diesel",
    payloadCapacity: 800,
    volumeCapacity: 12,
    hasFrigo: true,
    frigoType: "electrique",
    frigoBatteryAutonomy: 8,
    hasATP: true,
    hasTemperatureRecorder: true,
    defaultFuelTankCapacity: 70,
    controls: {
      technicalInspection: true,
      pollutionControl: true,
      atp: true,
      temperatureRecorder: true,
      extinguisher: true,
    },
    description: "Fourgon frigorifique groupe électrique",
    equipments: ["Groupe froid électrique", "Enregistreur T°"],
  },

  // ==========================================
  // PORTEURS FRIGORIFIQUES
  // ==========================================
  {
    id: "porteur_frigo_gasoil",
    name: "Porteur Frigo Gasoil",
    category: "PL Porteur Fourgon",
    image: "/bibliotheque_vehicules/05_Porteurs_Frigorifiques/image47.png",
    vehicleType: "pl_fourgon",
    fuelType: "diesel",
    payloadCapacity: 5000,
    volumeCapacity: 40,
    hasFrigo: true,
    frigoType: "gasoil",
    frigoTankCapacity: 50,
    hasATP: true,
    hasTemperatureRecorder: true,
    hasHayon: true,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 250,
    defaultAdBlueTankCapacity: 50,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      tailgate: true,
      atp: true,
      temperatureRecorder: true,
      extinguisher: true,
    },
    description: "Porteur frigorifique groupe gasoil",
    equipments: ["Groupe froid gasoil", "Enregistreur T°", "Hayon"],
  },

  // ==========================================
  // PORTEURS BENNE
  // ==========================================
  {
    id: "porteur_benne_8x4",
    name: "Camion Benne 8x4",
    category: "PL Porteur Plateau",
    image: "/bibliotheque_vehicules/06_Porteurs_Benne/image27.png",
    vehicleType: "pl_plateau",
    fuelType: "diesel",
    payloadCapacity: 15000,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 400,
    defaultAdBlueTankCapacity: 80,
    defaultAdBlueRatio: 6,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      extinguisher: true,
    },
    description: "Camion benne TP 8x4",
    equipments: ["Benne basculante", "Bâche"],
  },

  // ==========================================
  // PORTEURS CITERNE
  // ==========================================
  {
    id: "porteur_citerne",
    name: "Porteur Citerne ADR",
    category: "PL Porteur Citerne",
    image: "/bibliotheque_vehicules/07_Porteurs_Citerne/image26.png",
    vehicleType: "pl_citerne",
    fuelType: "diesel",
    hasCiterne: true,
    hasADR: true,
    hasTachygraphe: true,
    defaultFuelTankCapacity: 300,
    defaultAdBlueTankCapacity: 60,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      adr: true,
      adrIntermediate: true,
      adrPeriodic: true,
      adrHoses: true,
      extinguisher: true,
    },
    description: "Porteur citerne matières dangereuses",
    equipments: ["Citerne ADR", "Pompe", "Flexibles"],
  },

  // ==========================================
  // TRACTEURS ROUTIERS
  // ==========================================
  {
    id: "tracteur_4x2",
    name: "Tracteur 4x2",
    category: "Tracteur Routier",
    image: "/bibliotheque_vehicules/11_Tracteurs_Routiers/image33.png",
    vehicleType: "tracteur_routier",
    fuelType: "diesel",
    hasTachygraphe: true,
    defaultFuelTankCapacity: 400,
    defaultAdBlueTankCapacity: 60,
    defaultAdBlueRatio: 5,
    controls: {
      technicalInspectionPL: true,
      tachograph: true,
      extinguisher: true,
    },
    description: "Tracteur routier 4x2",
    equipments: ["Sellette", "Prises pneumatiques"],
  },

  // ==========================================
  // SEMI-REMORQUES
  // ==========================================
  {
    id: "semi_fourgon",
    name: "Semi Fourgon",
    category: "Semi-remorque Fourgon",
    image: "/bibliotheque_vehicules/12_Semi_Remorques_Fourgon/image34.png",
    vehicleType: "semi_fourgon",
    fuelType: null,
    volumeCapacity: 90,
    innerLength: 13.60,
    innerWidth: 2.45,
    innerHeight: 2.70,
    palletsEurope: 33,
    palletsISO: 26,
    hasHayon: true,
    controls: {
      technicalInspection: true,
      tailgate: true,
    },
    description: "Semi-remorque fourgon standard",
    equipments: ["Hayon élévateur", "Rails aluminium"],
  },
  {
    id: "semi_frigo",
    name: "Semi Frigorifique",
    category: "Semi-remorque Fourgon",
    image: "/bibliotheque_vehicules/13_Semi_Remorques_Frigo/image12.png",
    vehicleType: "semi_fourgon",
    fuelType: null,
    volumeCapacity: 85,
    innerLength: 13.40,
    innerWidth: 2.45,
    innerHeight: 2.60,
    palletsEurope: 33,
    hasFrigo: true,
    frigoType: "gasoil",
    frigoTankCapacity: 300,
    hasATP: true,
    hasTemperatureRecorder: true,
    hasHayon: true,
    controls: {
      technicalInspection: true,
      tailgate: true,
      atp: true,
      temperatureRecorder: true,
    },
    description: "Semi frigorifique ATP",
    equipments: ["Groupe froid", "Enregistreur T°"],
  },

  // ==========================================
  // REMORQUES
  // ==========================================
  {
    id: "remorque_fourgon",
    name: "Remorque Fourgon",
    category: "Remorque Fourgon",
    image: "/bibliotheque_vehicules/17_Remorques/image1.png",
    vehicleType: "remorque_fourgon",
    fuelType: null,
    volumeCapacity: 30,
    palletsEurope: 8,
    controls: {
      technicalInspection: true,
    },
    description: "Remorque fourgon",
    equipments: ["Portes arrière"],
  },
];

// Grouper les templates par catégorie
export const templatesByCategory = vehicleTemplates.reduce((acc, template) => {
  if (!acc[template.category]) {
    acc[template.category] = [];
  }
  acc[template.category].push(template);
  return acc;
}, {} as Record<string, VehicleTemplate[]>);

// Liste des catégories dans l'ordre
export const categories = [
  "VUL - Utilitaires < 3T5",
  "PL Porteur Fourgon",
  "PL Porteur Citerne",
  "PL Porteur Plateau",
  "Tracteur Routier",
  "Semi-remorque Fourgon",
  "Semi-remorque Citerne",
  "Semi-remorque Plateau",
  "Remorque Fourgon",
  "Remorque Citerne",
  "Remorque Plateau",
  "VL / Autocar",
];

// Configuration des catégories avec image représentative
export interface CategoryConfig {
  name: string;
  shortName: string;
  description: string;
  representativeImage: string;
  color: string;
}

export const categoryConfigs: CategoryConfig[] = [
  {
    name: "VUL - Utilitaires < 3T5",
    shortName: "VUL",
    description: "Fourgons utilitaires légers",
    representativeImage: "/bibliotheque_vehicules/02_VUL_Fourgons/image21.png",
    color: "#2196F3",
  },
  {
    name: "PL Porteur Fourgon",
    shortName: "PL Fourgon",
    description: "Porteurs poids lourds fourgon",
    representativeImage: "/bibliotheque_vehicules/04_Porteurs_Fourgon/image25.png",
    color: "#FF9800",
  },
  {
    name: "PL Porteur Citerne",
    shortName: "PL Citerne",
    description: "Porteurs poids lourds citerne",
    representativeImage: "/bibliotheque_vehicules/07_Porteurs_Citerne/image26.png",
    color: "#F44336",
  },
  {
    name: "PL Porteur Plateau",
    shortName: "PL Plateau",
    description: "Porteurs poids lourds plateau / benne",
    representativeImage: "/bibliotheque_vehicules/06_Porteurs_Benne/image27.png",
    color: "#795548",
  },
  {
    name: "Tracteur Routier",
    shortName: "Tracteur",
    description: "Tracteurs routiers",
    representativeImage: "/bibliotheque_vehicules/11_Tracteurs_Routiers/image33.png",
    color: "#3F51B5",
  },
  {
    name: "Semi-remorque Fourgon",
    shortName: "Semi Fourgon",
    description: "Semi-remorques fourgon",
    representativeImage: "/bibliotheque_vehicules/12_Semi_Remorques_Fourgon/image34.png",
    color: "#009688",
  },
  {
    name: "Semi-remorque Citerne",
    shortName: "Semi Citerne",
    description: "Semi-remorques citerne",
    representativeImage: "/bibliotheque_vehicules/12_Semi_Remorques_Fourgon/image34.png",
    color: "#00BCD4",
  },
  {
    name: "Semi-remorque Plateau",
    shortName: "Semi Plateau",
    description: "Semi-remorques plateau",
    representativeImage: "/bibliotheque_vehicules/12_Semi_Remorques_Fourgon/image34.png",
    color: "#03A9F4",
  },
  {
    name: "Remorque Fourgon",
    shortName: "Rem. Fourgon",
    description: "Remorques fourgon",
    representativeImage: "/bibliotheque_vehicules/17_Remorques/image1.png",
    color: "#78909C",
  },
  {
    name: "Remorque Citerne",
    shortName: "Rem. Citerne",
    description: "Remorques citerne",
    representativeImage: "/bibliotheque_vehicules/17_Remorques/image1.png",
    color: "#607D8B",
  },
  {
    name: "Remorque Plateau",
    shortName: "Rem. Plateau",
    description: "Remorques plateau",
    representativeImage: "/bibliotheque_vehicules/17_Remorques/image1.png",
    color: "#455A64",
  },
  {
    name: "VL / Autocar",
    shortName: "VL/Autocar",
    description: "Voitures de service, autocars",
    representativeImage: "/bibliotheque_vehicules/01_VL_Voitures/image10.png",
    color: "#4CAF50",
  },
];

export default vehicleTemplates;
